import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Mail, Send, Inbox, RefreshCw, Search, Loader2, 
  ChevronLeft, Paperclip, X, ExternalLink, AlertCircle,
  Building2, User, Trash2, Download, FileText, Image, File,
  FolderOpen, Link, Plus, FileIcon
} from 'lucide-react';
import * as api from '../services/api';

const GmailPanel = ({ currentUser, empresas = [], contactos = [], emailTemplates = [] }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState(false);
  
  // Compose modal state
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState({
    to: '',
    subject: '',
    body: '',
    cc: '',
    bcc: ''
  });
  const [sending, setSending] = useState(false);
  
  // Company/Contact search state
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [empresaContacts, setEmpresaContacts] = useState([]);
  const [selectedContacto, setSelectedContacto] = useState(null);
  const [showEmpresaDropdown, setShowEmpresaDropdown] = useState(false);
  
  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateAttachments, setTemplateAttachments] = useState([]);
  
  // File attachments state
  const [fileAttachments, setFileAttachments] = useState([]);
  
  // Drive search state
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [driveSearch, setDriveSearch] = useState('');
  const [driveFiles, setDriveFiles] = useState([]);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const [driveAttachments, setDriveAttachments] = useState([]);

  // Filtered empresas based on search
  const filteredEmpresas = empresas.filter(e => 
    empresaSearch.length >= 1 && 
    (e.name?.toLowerCase().includes(empresaSearch.toLowerCase()) ||
     e.email?.toLowerCase().includes(empresaSearch.toLowerCase()))
  ).slice(0, 10);

  const loadEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.listGmailMessages(currentUser.id, 30, searchQuery || null);
      setEmails(response.data || []);
    } catch (err) {
      console.error('Error loading emails:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('not connected')) {
        setError('Gmail no está conectado. Ve a Admin > Google para conectar tu cuenta.');
      } else {
        setError('Error cargando emails: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser.id, searchQuery]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  // Load contacts when empresa changes
  useEffect(() => {
    if (selectedEmpresa) {
      const contacts = selectedEmpresa.contacts || [];
      setEmpresaContacts(contacts);
      // Also check global contactos that belong to this empresa
      const globalContacts = contactos.filter(c => c.empresa_id === selectedEmpresa.id);
      const allContacts = [...contacts, ...globalContacts.filter(gc => !contacts.find(c => c.id === gc.id))];
      setEmpresaContacts(allContacts);
    } else {
      setEmpresaContacts([]);
    }
  }, [selectedEmpresa, contactos]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadEmails();
  };

  const openEmail = async (emailId) => {
    try {
      setLoadingEmail(true);
      const response = await api.getGmailMessage(currentUser.id, emailId);
      setSelectedEmail(response.data);
    } catch (err) {
      console.error('Error loading email:', err);
      setError('Error cargando email');
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('¿Estás seguro de eliminar este correo?')) return;
    
    try {
      setDeletingEmail(true);
      await api.deleteGmailMessage(currentUser.id, emailId);
      setSelectedEmail(null);
      loadEmails();
    } catch (err) {
      console.error('Error deleting email:', err);
      alert('Error eliminando email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingEmail(false);
    }
  };

  const downloadAttachment = async (messageId, attachmentId, filename) => {
    try {
      const response = await api.getGmailAttachment(currentUser.id, messageId, attachmentId);
      const data = response.data.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(data.replace(/-/g, '+').replace(/_/g, '/'));
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      
      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      alert('Error descargando archivo');
    }
  };

  const handleSend = async () => {
    if (!composeData.to || !composeData.subject) {
      alert('Por favor ingresa destinatario y asunto');
      return;
    }
    
    try {
      setSending(true);
      
      // Prepare all attachments
      const allAttachments = [...fileAttachments];
      
      // Add template attachments (convert to File-like objects if needed)
      const templateAtts = templateAttachments.map(att => ({
        filename: att.filename,
        file_data: att.file_data,
        content_type: att.content_type
      }));
      
      // Add Drive links to body if any
      let bodyWithDriveLinks = composeData.body;
      if (driveAttachments.length > 0) {
        bodyWithDriveLinks += '\n\n--- Archivos adjuntos de Google Drive ---\n';
        driveAttachments.forEach(file => {
          bodyWithDriveLinks += `📎 ${file.name}: ${file.webViewLink}\n`;
        });
      }
      
      // If we have file attachments or template attachments, use the multipart endpoint
      if (allAttachments.length > 0 || templateAtts.length > 0) {
        const emailPayload = {
          sender_id: currentUser.id,
          sender_name: currentUser.name,
          contact_email: composeData.to,
          subject: composeData.subject,
          body: bodyWithDriveLinks,
          empresa_id: selectedEmpresa?.id || null,
          empresa_name: selectedEmpresa?.name || null,
          contact_id: selectedContacto?.id || null,
          contact_name: selectedContacto?.name || null,
          template_id: selectedTemplate || null,
          template_attachments: templateAtts
        };
        
        await api.sendEmailWithAttachments(emailPayload, allAttachments);
      } else {
        // Use simple Gmail send
        await api.sendGmail(currentUser.id, {
          to: composeData.to,
          subject: composeData.subject,
          body: bodyWithDriveLinks,
          cc: composeData.cc || null,
          bcc: composeData.bcc || null
        });
      }
      
      resetComposeForm();
      loadEmails();
      alert('Email enviado exitosamente');
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMsg = err.response?.data?.detail || err.message;
      alert('Error: ' + errorMsg);
    } finally {
      setSending(false);
    }
  };

  const resetComposeForm = () => {
    setShowCompose(false);
    setComposeData({ to: '', subject: '', body: '', cc: '', bcc: '' });
    setSelectedEmpresa(null);
    setSelectedContacto(null);
    setEmpresaSearch('');
    setSelectedTemplate('');
    setTemplateAttachments([]);
    setFileAttachments([]);
    setDriveAttachments([]);
  };

  const openComposeWithRecipient = (email, name) => {
    setComposeData({
      to: email,
      subject: '',
      body: `Hola ${name || ''},\n\n`,
      cc: '',
      bcc: ''
    });
    setShowCompose(true);
  };

  const handleSelectEmpresa = (empresa) => {
    setSelectedEmpresa(empresa);
    setEmpresaSearch(empresa.name);
    setShowEmpresaDropdown(false);
    setSelectedContacto(null);
    // Don't auto-fill email - let user select a contact
  };

  const handleSelectContacto = (contacto) => {
    setSelectedContacto(contacto);
    if (contacto?.email) {
      setComposeData(prev => ({ 
        ...prev, 
        to: contacto.email,
        body: prev.body || `Hola ${contacto.name || ''},\n\n`
      }));
    }
  };

  const handleSelectTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      let subject = template.subject;
      let body = template.body;
      
      // Replace variables
      if (selectedEmpresa) {
        subject = subject.replace(/\{\{empresa_name\}\}/g, selectedEmpresa.name || '');
        body = body.replace(/\{\{empresa_name\}\}/g, selectedEmpresa.name || '');
      }
      if (selectedContacto) {
        subject = subject.replace(/\{\{contacto_name\}\}/g, selectedContacto.name || '');
        body = body.replace(/\{\{contacto_name\}\}/g, selectedContacto.name || '');
      }
      
      setComposeData(prev => ({
        ...prev,
        subject,
        body
      }));
      
      // Set template attachments
      setTemplateAttachments(template.attachments || []);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFileAttachments(prev => [...prev, ...files]);
    e.target.value = ''; // Reset input
  };

  const removeFileAttachment = (index) => {
    setFileAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeTemplateAttachment = (index) => {
    setTemplateAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeDriveAttachment = (index) => {
    setDriveAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Drive file search
  const searchDriveFiles = async () => {
    if (!driveSearch.trim()) return;
    
    try {
      setLoadingDrive(true);
      const response = await api.listDriveFiles(null, driveSearch, 20);
      // API returns array directly, not { files: [...] }
      setDriveFiles(Array.isArray(response.data) ? response.data : (response.data?.files || []));
    } catch (err) {
      console.error('Error searching Drive:', err);
      alert('Error buscando archivos en Drive: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingDrive(false);
    }
  };

  const addDriveFile = (file) => {
    if (!driveAttachments.find(f => f.id === file.id)) {
      setDriveAttachments(prev => [...prev, file]);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      
      if (isToday) {
        return date.toLocaleTimeString('es-PR', { hour: '2-digit', minute: '2-digit' });
      }
      return date.toLocaleDateString('es-PR', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-PR', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const extractName = (fromStr) => {
    if (!fromStr) return '';
    const match = fromStr.match(/^([^<]+)/);
    return match ? match[1].trim().replace(/"/g, '') : fromStr;
  };

  const getAttachmentIcon = (mimeType) => {
    if (!mimeType) return <File className="h-4 w-4 text-gray-400" />;
    if (mimeType.startsWith('image/')) return <Image className="h-4 w-4 text-blue-500" />;
    if (mimeType.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="h-4 w-4 text-blue-600" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FileText className="h-4 w-4 text-green-600" />;
    return <File className="h-4 w-4 text-gray-400" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-red-500" />
          <h2 className="text-lg font-semibold">Gmail</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadEmails} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button size="sm" className="bg-red-500 hover:bg-red-600" onClick={() => setShowCompose(true)}>
            <Mail className="h-4 w-4 mr-1" />
            Redactar
          </Button>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar en emails..."
            className="pl-10"
          />
        </div>
        <Button type="submit" variant="outline">Buscar</Button>
      </form>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Email List/Detail */}
      <Card>
        <CardContent className="p-0">
          {selectedEmail ? (
            // Email Detail View
            <div>
              {/* Detail Header */}
              <div className="p-4 border-b flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Volver
                </Button>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg truncate">{selectedEmail.subject || '(Sin asunto)'}</h3>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openComposeWithRecipient(
                    selectedEmail.from?.match(/<(.+)>/)?.[1] || selectedEmail.from,
                    extractName(selectedEmail.from)
                  )}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Responder
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteEmail(selectedEmail.id)}
                  disabled={deletingEmail}
                >
                  {deletingEmail ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Sender Info */}
              <div className="p-4 border-b">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-semibold text-lg flex-shrink-0">
                    {extractName(selectedEmail.from)?.[0]?.toUpperCase() || 'E'}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{extractName(selectedEmail.from)}</p>
                    <p className="text-sm text-gray-500">{selectedEmail.from?.match(/<(.+)>/)?.[1] || selectedEmail.from}</p>
                    <div className="text-sm text-gray-500 mt-1">
                      <p>Para: {selectedEmail.to}</p>
                      {selectedEmail.cc && <p>CC: {selectedEmail.cc}</p>}
                      <p className="text-xs mt-1">{formatFullDate(selectedEmail.date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {selectedEmail.attachments.length} archivo(s) adjunto(s)
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedEmail.attachments.map((att, idx) => (
                      <button
                        key={idx}
                        onClick={() => downloadAttachment(selectedEmail.id, att.id, att.filename)}
                        className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {getAttachmentIcon(att.mimeType)}
                        <span className="text-sm truncate max-w-32">{att.filename}</span>
                        <span className="text-xs text-gray-400">{formatFileSize(att.size)}</span>
                        <Download className="h-3 w-3 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Body */}
              <div className="p-4">
                {loadingEmail ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : selectedEmail.html_body ? (
                  <div 
                    className="email-content prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedEmail.html_body, {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'img'],
                      ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'target', 'width', 'height'],
                      ALLOW_DATA_ATTR: false
                    }) }}
                    style={{ overflow: 'auto', maxHeight: '60vh' }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {selectedEmail.body || selectedEmail.snippet || '(Sin contenido)'}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Email List View
            <div className="divide-y">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                  <p className="text-gray-500 mt-2">Cargando emails...</p>
                </div>
              ) : emails.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Inbox className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  No se encontraron emails
                </div>
              ) : (
                emails.map((email) => (
                  <div
                    key={email.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer flex items-start gap-3 group"
                  >
                    <div 
                      onClick={() => openEmail(email.id)}
                      className="flex items-start gap-3 flex-1"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium flex-shrink-0">
                        {extractName(email.from)?.[0]?.toUpperCase() || 'E'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`font-medium truncate ${!email.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                            {extractName(email.from)}
                          </p>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatDate(email.date)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${!email.isRead ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                          {email.subject || '(Sin asunto)'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{email.snippet}</p>
                        {email.hasAttachments && (
                          <Paperclip className="h-3 w-3 text-gray-400 mt-1" />
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEmail(email.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded text-red-500 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compose Dialog */}
      <Dialog open={showCompose} onOpenChange={(open) => { if (!open) resetComposeForm(); else setShowCompose(true); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-red-500" />
              Nuevo Correo
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Company Search */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  <Building2 className="h-3 w-3 inline mr-1" />
                  Empresa
                </label>
                <Input
                  value={empresaSearch}
                  onChange={(e) => {
                    setEmpresaSearch(e.target.value);
                    setShowEmpresaDropdown(true);
                    if (!e.target.value) {
                      setSelectedEmpresa(null);
                      setEmpresaContacts([]);
                    }
                  }}
                  onFocus={() => setShowEmpresaDropdown(true)}
                  onBlur={() => setTimeout(() => setShowEmpresaDropdown(false), 200)}
                  placeholder="Buscar empresa..."
                  data-testid="gmail-empresa-search"
                />
                {showEmpresaDropdown && filteredEmpresas.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredEmpresas.map((emp) => (
                      <button
                        key={emp.id}
                        onClick={() => handleSelectEmpresa(emp)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="font-medium">{emp.name}</p>
                          {emp.email && <p className="text-xs text-gray-500">{emp.email}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  <User className="h-3 w-3 inline mr-1" />
                  Contacto
                </label>
                <Select 
                  value={selectedContacto?.id || ''} 
                  onValueChange={(id) => {
                    const contact = empresaContacts.find(c => c.id === id);
                    handleSelectContacto(contact);
                  }}
                  disabled={!selectedEmpresa}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={selectedEmpresa ? "Seleccionar contacto" : "Primero selecciona empresa"} />
                  </SelectTrigger>
                  <SelectContent>
                    {empresaContacts.filter(c => c.email).map((cont) => (
                      <SelectItem key={cont.id} value={cont.id}>
                        {cont.name} - {cont.email}
                      </SelectItem>
                    ))}
                    {empresaContacts.filter(c => c.email).length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No hay contactos con email
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Template selector */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                <FileText className="h-3 w-3 inline mr-1" />
                Plantilla (opcional)
              </label>
              <Select value={selectedTemplate} onValueChange={handleSelectTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar plantilla" />
                </SelectTrigger>
                <SelectContent>
                  {emailTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                      {template.attachments?.length > 0 && (
                        <span className="ml-2 text-xs text-gray-400">
                          ({template.attachments.length} adjunto{template.attachments.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* To field */}
            <div>
              <label className="text-sm font-medium text-gray-700">Para</label>
              <Input
                value={composeData.to}
                onChange={(e) => setComposeData({...composeData, to: e.target.value})}
                placeholder="destinatario@email.com"
              />
            </div>
            
            {/* CC/BCC */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">CC (opcional)</label>
                <Input
                  value={composeData.cc}
                  onChange={(e) => setComposeData({...composeData, cc: e.target.value})}
                  placeholder="cc@email.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">BCC (opcional)</label>
                <Input
                  value={composeData.bcc}
                  onChange={(e) => setComposeData({...composeData, bcc: e.target.value})}
                  placeholder="bcc@email.com"
                />
              </div>
            </div>
            
            {/* Subject */}
            <div>
              <label className="text-sm font-medium text-gray-700">Asunto</label>
              <Input
                value={composeData.subject}
                onChange={(e) => setComposeData({...composeData, subject: e.target.value})}
                placeholder="Asunto del correo"
              />
            </div>
            
            {/* Body */}
            <div>
              <label className="text-sm font-medium text-gray-700">Mensaje</label>
              <textarea
                value={composeData.body}
                onChange={(e) => setComposeData({...composeData, body: e.target.value})}
                className="w-full h-40 p-3 border rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Escribe tu mensaje..."
              />
            </div>

            {/* Attachments Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <Paperclip className="h-4 w-4" />
                  Archivos Adjuntos
                </label>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Plus className="h-4 w-4 mr-1" />
                        Adjuntar Archivo
                      </span>
                    </Button>
                  </label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowDrivePicker(true)}
                  >
                    <FolderOpen className="h-4 w-4 mr-1" />
                    Desde Drive
                  </Button>
                </div>
              </div>

              {/* Template attachments */}
              {templateAttachments.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-1">De la plantilla:</p>
                  <div className="flex flex-wrap gap-2">
                    {templateAttachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm">
                        <FileText className="h-3 w-3 text-blue-500" />
                        <span className="truncate max-w-32">{att.filename}</span>
                        <button onClick={() => removeTemplateAttachment(idx)} className="text-blue-500 hover:text-blue-700">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File attachments */}
              {fileAttachments.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-500 mb-1">Archivos adjuntos:</p>
                  <div className="flex flex-wrap gap-2">
                    {fileAttachments.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 border rounded text-sm">
                        <FileIcon className="h-3 w-3 text-gray-500" />
                        <span className="truncate max-w-32">{file.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                        <button onClick={() => removeFileAttachment(idx)} className="text-gray-500 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Drive attachments */}
              {driveAttachments.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Enlaces de Google Drive:</p>
                  <div className="flex flex-wrap gap-2">
                    {driveAttachments.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-sm">
                        <Link className="h-3 w-3 text-green-500" />
                        <span className="truncate max-w-32">{file.name}</span>
                        <button onClick={() => removeDriveAttachment(idx)} className="text-green-500 hover:text-green-700">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {templateAttachments.length === 0 && fileAttachments.length === 0 && driveAttachments.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-2">No hay archivos adjuntos</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetComposeForm}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSend} 
              disabled={sending}
              className="bg-red-500 hover:bg-red-600"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drive File Picker Dialog */}
      <Dialog open={showDrivePicker} onOpenChange={setShowDrivePicker}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-yellow-500" />
              Seleccionar archivo de Google Drive
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={driveSearch}
                onChange={(e) => setDriveSearch(e.target.value)}
                placeholder="Buscar archivos en Drive..."
                onKeyDown={(e) => e.key === 'Enter' && searchDriveFiles()}
              />
              <Button onClick={searchDriveFiles} disabled={loadingDrive}>
                {loadingDrive ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>

            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {driveFiles.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <FolderOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>Busca archivos en tu Drive</p>
                </div>
              ) : (
                <div className="divide-y">
                  {driveFiles.map((file) => (
                    <button
                      key={file.id}
                      onClick={() => addDriveFile(file)}
                      disabled={driveAttachments.find(f => f.id === file.id)}
                      className="w-full p-3 text-left hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50"
                    >
                      {getAttachmentIcon(file.mimeType)}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.modifiedTime && formatDate(file.modifiedTime)}</p>
                      </div>
                      {driveAttachments.find(f => f.id === file.id) && (
                        <Badge variant="secondary">Agregado</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDrivePicker(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Styles for HTML email content */}
      <style>{`
        .email-content img {
          max-width: 100%;
          height: auto;
        }
        .email-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        .email-content table {
          border-collapse: collapse;
          width: 100%;
        }
        .email-content td, .email-content th {
          border: 1px solid #e5e7eb;
          padding: 8px;
        }
      `}</style>
    </div>
  );
};

export default GmailPanel;
